import { z } from 'zod';
import { CALENDAR_DATE } from '../time/agency-dates.js';
import { isWellFormedAccessCode } from '../portal/codes.js';
/**
 * The mail model (#61): what an email to a person can be made of, and nothing else.
 *
 * Everything here is closed on purpose. A closed template vocabulary can be enumerated in a
 * test; a closed merge vocabulary can be rendered as an editable form and can never reach an
 * SSN, a TB answer or a bank account, because there is no key for one. An expression
 * language could be neither — the same reasoning ADR-006 gives for requirement rules.
 *
 * There is deliberately NO attachment field anywhere on these types. PLAN §7.5 says links,
 * not attachments, and the cheapest way to keep that true for ever is for the shape to
 * have no place to put a file. The schemas below are strict, so an object that turns up
 * carrying `attachments` — or anything else — is refused rather than trimmed.
 */
export const templateIds = ['portal-invite', 'offer-invite', 'doc-request', 'link-reissue', 'self-service-reissue', 'rejection-notice', 'custom'];
export const templateIdSchema = z.enum(templateIds);
/** The tokens a template may carry, written `{{firstName}}`. */
export const mergeTokens = [
    'firstName',
    'preferredName',
    'fullName',
    'dueDate',
    'items',
    'portalLink',
    'accessCode',
    'hrContact',
    'position',
    'acceptBy',
];
/**
 * What a render may be given. Every key optional — a template only needs the ones it
 * names — but a key that IS present must carry something usable, and no other key exists.
 *
 * `accessCode` is held to the portal's own code shape and `portalLink` to a URL, so neither
 * can carry a raw bearer token by accident: the mail layer never mints or sees one.
 */
export const mergeContextSchema = z
    .object({
    firstName: z.string().min(1).optional(),
    preferredName: z.string().min(1).optional(),
    fullName: z.string().min(1).optional(),
    /** A calendar date in the agency zone, `YYYY-MM-DD`; rendered as given. */
    dueDate: z.string().regex(CALENDAR_DATE).optional(),
    /** Checklist item labels, in the order they should be read. */
    items: z.array(z.string().min(1)).min(1).optional(),
    /** https only: `new URL()` accepts `javascript:` too, and this lands in an href. */
    portalLink: z.string().url().refine((u) => u.startsWith('https://'), 'portal links are https').optional(),
    accessCode: z.string().refine(isWellFormedAccessCode, 'not an access code').optional(),
    hrContact: z.string().min(1).optional(),
    /** The offered job title, from the reviewed offer's own terms — never a Person field (#262). */
    position: z.string().min(1).optional(),
    /**
     * The acceptance deadline as the candidate should read it, e.g. `Sep 21, 2026, 5:00 PM`.
     *
     * A display string, not an instant, and deliberately not `dueDate`: that token is a
     * `YYYY-MM-DD` calendar date rendered as given, and frame 297:932 writes a date AND a
     * time, because an offer expires at an hour. Formatting happens in the caller, where the
     * agency timezone is known; this layer stays clock-free and locale-free (see renderMail).
     */
    acceptBy: z.string().min(1).optional(),
})
    .strict();
/**
 * A template: subject, HTML body and text body, each free to name tokens. Strict, so a
 * template carrying an attachment key is not a template.
 */
export const mailTemplateSchema = z
    .object({
    id: templateIdSchema,
    subject: z.string().min(1),
    html: z.string().min(1),
    text: z.string().min(1),
})
    .strict();
/**
 * The Phase 2 templates, and the one id that has no copy of its own.
 *
 * `custom` is what HR writes themselves in the composer (a later ticket). It is in the
 * vocabulary so a Communication can name it, and in this registry so the `Record` contract
 * still covers every id — but as `'authored'`, not as a message: inventing a generic
 * production email here would be exactly the copy nobody reviewed.
 *
 * `satisfies Record<TemplateId, …>`: remove an entry and the build fails naming the id.
 */
export const mailTemplates = {
    'portal-invite': {
        id: 'portal-invite',
        subject: 'Your Vigeo Home Health onboarding checklist',
        html: '<p>Hi {{firstName}},</p>' +
            '<p>Welcome to Vigeo Home Health. Your onboarding checklist is ready. Open the link below and enter your access code to get started.</p>' +
            '<p><a href="{{portalLink}}">Open your checklist</a><br>Access code: <strong>{{accessCode}}</strong></p>' +
            '<p>Questions? Reply to this email or contact {{hrContact}}.</p>',
        text: 'Hi {{firstName}},\n\n' +
            'Welcome to Vigeo Home Health. Your onboarding checklist is ready. Open the link below and enter your access code to get started.\n\n' +
            'Open your checklist: {{portalLink}}\nAccess code: {{accessCode}}\n\n' +
            'Questions? Reply to this email or contact {{hrContact}}.',
    },
    /**
     * The offer email (#262), worded as frame 297:932 draws it.
     *
     * Distinct from `portal-invite` even though both carry a link and a code. That one welcomes
     * someone to a checklist; this one names a job, a deadline and a document to sign, and the
     * frame's subject line is the difference between an email a candidate opens and one they
     * take for onboarding admin. It is also why `position` exists as a token at all.
     *
     * NO ATTACHMENT, by the shape of these types: the letter stays behind the access code, which
     * is what makes the portal — not an inbox — the place the signature is collected.
     */
    'offer-invite': {
        id: 'offer-invite',
        subject: 'Congratulations! Your offer from Vigeo Home Health',
        html: '<p>Hi {{firstName}},</p>' +
            '<p>Congratulations! We’re pleased to offer you the {{position}} position at Vigeo Home Health.</p>' +
            '<p>Your offer letter is ready in your secure portal. Open the link below, enter your access code, then read the letter and sign it by {{acceptBy}}.</p>' +
            '<p><a href="{{portalLink}}">Review and sign your offer letter</a><br>Access code: <strong>{{accessCode}}</strong></p>' +
            '<p>Questions about your offer? Reply to this email or contact {{hrContact}}.</p>' +
            '<p>We look forward to welcoming you to the team.</p>' +
            '<p>Vigeo Home Health HR</p>',
        text: 'Hi {{firstName}},\n\n' +
            'Congratulations! We’re pleased to offer you the {{position}} position at Vigeo Home Health.\n\n' +
            'Your offer letter is ready in your secure portal. Open the link below, enter your access code, then read the letter and sign it by {{acceptBy}}.\n\n' +
            'Review and sign your offer letter: {{portalLink}}\nAccess code: {{accessCode}}\n\n' +
            'Questions about your offer? Reply to this email or contact {{hrContact}}.\n\n' +
            'We look forward to welcoming you to the team.\n\n' +
            'Vigeo Home Health HR',
    },
    'doc-request': {
        id: 'doc-request',
        subject: 'Documents we still need from you',
        html: '<p>Hi {{firstName}},</p>' +
            '<p>To keep your file complete, we still need the following by {{dueDate}}:</p>' +
            '{{items}}' +
            '<p><a href="{{portalLink}}">Open your checklist</a><br>Access code: <strong>{{accessCode}}</strong></p>' +
            '<p>Questions? Contact {{hrContact}}.</p>',
        text: 'Hi {{firstName}},\n\n' +
            'To keep your file complete, we still need the following by {{dueDate}}:\n\n' +
            '{{items}}\n\n' +
            'Open your checklist: {{portalLink}}\nAccess code: {{accessCode}}\n\n' +
            'Questions? Contact {{hrContact}}.',
    },
    'link-reissue': {
        id: 'link-reissue',
        subject: 'A new link to your onboarding checklist',
        html: '<p>Hi {{firstName}},</p>' +
            '<p>Here is a new link to your checklist. Your previous link no longer works.</p>' +
            '<p><a href="{{portalLink}}">Open your checklist</a><br>Access code: <strong>{{accessCode}}</strong></p>' +
            '<p>If you did not ask for this, contact {{hrContact}}.</p>',
        text: 'Hi {{firstName}},\n\n' +
            'Here is a new link to your checklist. Your previous link no longer works.\n\n' +
            'Open your checklist: {{portalLink}}\nAccess code: {{accessCode}}\n\n' +
            'If you did not ask for this, contact {{hrContact}}.',
    },
    /**
     * The self-service reissue (#82): the LINK ONLY. It keeps the person's existing access code,
     * which reached them by a separate channel and is never put in an email (#61 decision 1).
     * Distinct from `link-reissue`, which HR delivery sends with a freshly minted code.
     */
    'self-service-reissue': {
        id: 'self-service-reissue',
        subject: 'A new link to your onboarding checklist',
        html: '<p>Hi {{firstName}},</p>' +
            '<p>Here is the new link you asked for. Your previous link no longer works.</p>' +
            '<p><a href="{{portalLink}}">Open your checklist</a></p>' +
            '<p>Use the access code you were given with your original link. We never send it by email. If you no longer have it, or you did not ask for this, contact {{hrContact}}.</p>',
        text: 'Hi {{firstName}},\n\n' +
            'Here is the new link you asked for. Your previous link no longer works.\n\n' +
            'Open your checklist: {{portalLink}}\n\n' +
            'Use the access code you were given with your original link. We never send it by email. If you no longer have it, or you did not ask for this, contact {{hrContact}}.',
    },
    'rejection-notice': {
        id: 'rejection-notice',
        subject: 'One item on your checklist needs another look',
        html: '<p>Hi {{firstName}},</p>' +
            '<p>We could not accept the following and need you to send it again:</p>' +
            '{{items}}' +
            '<p>Please return to your checklist using the link and access code you already received, and send it again from there.</p>' +
            '<p>If you no longer have your link or code, contact {{hrContact}} and we will send you a new one.</p>',
        text: 'Hi {{firstName}},\n\n' +
            'We could not accept the following and need you to send it again:\n\n' +
            '{{items}}\n\n' +
            'Please return to your checklist using the link and access code you already received, and send it again from there.\n\n' +
            'If you no longer have your link or code, contact {{hrContact}} and we will send you a new one.',
    },
    custom: 'authored',
};
export function templateFor(id) {
    return mailTemplates[id];
}
/**
 * A `custom` template from text HR wrote. The body is escaped and paragraphed for HTML and
 * kept as written for text; tokens inside it render like any other template's, so a
 * composer can offer the same merge fields.
 */
export function authoredTemplate(subject, body) {
    const paragraphs = body
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter((p) => p !== '')
        .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
        .join('');
    return mailTemplateSchema.parse({ id: 'custom', subject, html: paragraphs, text: body });
}
/**
 * The five characters that can turn text into markup. Applied to every merged value and to
 * authored bodies, so a person's own name — or anything a person typed — cannot become an
 * element. Tokens are re-inserted AFTER escaping the surrounding authored text, so the
 * braces survive to be resolved.
 */
export function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
//# sourceMappingURL=templates.js.map